import { captureException } from '@sentry/react-router';
import { useEffect, type ReactElement } from 'react';
import {
  type ErrorResponse,
  isRouteErrorResponse,
  useParams,
  useRouteError,
} from 'react-router';
import { getErrorMessage } from '#app/utils/misc';
import { shouldShowMaintenancePage } from '#app/utils/deployment-error-detector';

type StatusHandler = (info: {
  error: ErrorResponse;
  params: Record<string, string | undefined>;
}) => ReactElement | null;

export function GeneralErrorBoundary({
  defaultStatusHandler = ({ error }) => (
    <p>
      {error.status} {error.data}
    </p>
  ),
  statusHandlers,
  unexpectedErrorHandler = (error) => <p>{getErrorMessage(error)}</p>,
}: {
  defaultStatusHandler?: StatusHandler;
  statusHandlers?: Record<number, StatusHandler>;
  unexpectedErrorHandler?: (error: unknown) => ReactElement | null;
}) {
  const error = useRouteError();
  const params = useParams();
  const isResponse = isRouteErrorResponse(error);


  useEffect(() => {
    // Check deployment errors for both route responses and regular errors
    if (shouldShowMaintenancePage(error)) {
      window.location.href = '/deployment-maintenance';
      return;
    }

    // Only capture exceptions for non-response errors (existing logic)
    if (!isResponse) {
      captureException(error);
    }
  }, [error, isResponse]);

  return (
    <div className="containerx flexx items-center justify-center p-20x text-h2">
      {isResponse
        ? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
            error,
            params,
          })
        : unexpectedErrorHandler(error)}
    </div>
  );
}
